import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getProducts, getMarketplaces, deleteProduct, bulkDeleteProducts, deleteAllProducts, startKeepaPull, runUnmatched, getJobProgress, cancelJob } from '../api';
import CustomSelect from './CustomSelect';
import ColumnToggle from './ColumnToggle';
import KeepaPullModal from './KeepaPullModal';
import { useToast } from './Toast';
import { usePreferences } from '../PreferencesContext';

const PRODUCT_LIST_COLUMNS = [
  { key: 'thumbnail', label: 'Thumbnail' },
  { key: 'sku', label: 'SKU' },
  { key: 'title', label: 'Title' },
  { key: 'brand', label: 'Brand' },
  { key: 'ean', label: 'EAN' },
  { key: 'price', label: 'Price' },
  { key: 'buybox', label: 'Buy Box' },
  { key: 'fba_fee', label: 'FBA Fee' },
  { key: 'referral_fee', label: 'Referral Fee' },
  { key: 'profit_amount', label: 'Profit' },
  { key: 'profit_margin', label: 'Profit %' },
  { key: 'suggested', label: 'Suggested' },
  { key: 'status', label: 'Status' },
];

interface ProductListProps {
  onSelectProduct: (id: string) => void;
}

interface JobProgress {
  job_id: string;
  status: string;
  total: number;
  processed: number;
  matched: number;
  needs_review: number;
  no_match: number;
  failed: number;
  enriched: number;
  current_batch: number;
  total_batches: number;
  tokens_left: number | null;
  errors: string[];
  eta_seconds: number | null;
}

const ProductList: React.FC<ProductListProps> = ({ onSelectProduct }) => {
  const { showToast } = useToast();
  const { isColumnVisible } = usePreferences();
  const col = (key: string) => isColumnVisible('product_list', key);
  const [products, setProducts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightboxUrl) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxUrl(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxUrl]);

  // Debounce search input → searchFilter (fires API call)
  useEffect(() => {
    const t = setTimeout(() => { setSearchFilter(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Marketplace state
  const [marketplaces, setMarketplaces] = useState<any[]>([]);
  const [keepaMarketplace, setKeepaMarketplace] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('asin-tracker-settings') || '{}');
      return saved.defaultMarketplace || 'GB';
    } catch { return 'GB'; }
  });

  const getCurrencySymbolByCode = (code?: string) => {
    switch ((code || '').toUpperCase()) {
      case 'GB': return '£';
      case 'DE':
      case 'FR':
      case 'IT':
      case 'ES':
      case 'NL':
      case 'BE':
      case 'AT':
      case 'IE':
      case 'PL':
      case 'SE':
      case 'TR':
        return '€';
      case 'US': return '$';
      case 'JP': return '¥';
      case 'CA': return 'CA$';
      case 'AU': return 'A$';
      case 'MX': return 'MX$';
      case 'BR': return 'R$';
      case 'IN': return '₹';
      case 'SG': return 'S$';
      case 'AE': return 'AED ';
      case 'SA': return 'SAR ';
      default: return '$';
    }
  };

  // Keepa pull state
  const [keepaPulling, setKeepaPulling] = useState(false);
  const [keepaJobId, setKeepaJobId] = useState<string | null>(null);
  const [keepaProgress, setKeepaProgress] = useState<JobProgress | null>(null);
  const [pullModalOpen, setPullModalOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    getMarketplaces().then(res => setMarketplaces(res.data)).catch(() => {});
  }, []);

  // Resume polling if there's an active job from before browser close/refresh
  useEffect(() => {
    const savedJobId = localStorage.getItem('keepa-active-job-id');
    if (savedJobId) {
      setKeepaJobId(savedJobId);
      setKeepaPulling(true);
      // Check if it's still running
      getJobProgress(savedJobId).then(res => {
        setKeepaProgress(res.data);
        if (['completed', 'failed', 'cancelled'].includes(res.data.status)) {
          setKeepaPulling(false);
          localStorage.removeItem('keepa-active-job-id');
          loadProducts();
        } else {
          // Still running — resume polling
          pollRef.current = setInterval(async () => {
            try {
              const r = await getJobProgress(savedJobId);
              setKeepaProgress(r.data);
              if (['completed', 'failed', 'cancelled'].includes(r.data.status)) {
                stopPolling();
                setKeepaPulling(false);
                localStorage.removeItem('keepa-active-job-id');
                loadProducts();
              }
            } catch { /* retry */ }
          }, 2000);
        }
      }).catch(() => {
        // Job no longer exists (server restarted and job was lost)
        localStorage.removeItem('keepa-active-job-id');
        setKeepaPulling(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, searchFilter, keepaMarketplace, sortBy, sortDir]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: pageSize, marketplace_code: keepaMarketplace };
      if (statusFilter) params.status = statusFilter;
      if (searchFilter) params.search = searchFilter;
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const res = await getProducts(params);
      setProducts(res.data.items || []);
      setTotal(res.data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map((p) => p.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Delete ${count} selected product${count > 1 ? 's' : ''}? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      await bulkDeleteProducts(Array.from(selectedIds));
      showToast(`Deleted ${count} product${count > 1 ? 's' : ''}`, 'success');
      await loadProducts();
    } catch (err) {
      console.error('Failed to delete products:', err);
      showToast('Failed to delete products. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this product? This cannot be undone.')) return;

    setDeleting(true);
    try {
      await deleteProduct(id);
      showToast('Product deleted', 'success');
      await loadProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      showToast('Failed to delete product. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    const filterDesc = statusFilter || searchFilter
      ? ` matching current filters`
      : '';
    if (!window.confirm(`Delete ALL ${total} product${total !== 1 ? 's' : ''}${filterDesc}? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (searchFilter) params.search = searchFilter;
      const res = await deleteAllProducts(params);
      showToast(`Deleted ${res.data.deleted_count} products`, 'success');
      setPage(1);
      await loadProducts();
    } catch (err) {
      console.error('Failed to delete all products:', err);
      showToast('Failed to delete products. Please try again.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Keepa Pull ────────────────────────────────────────────────────────

  const pollKeepaProg = useCallback(async (jobId: string) => {
    try {
      const res = await getJobProgress(jobId);
      setKeepaProgress(res.data);
      if (['completed', 'failed', 'cancelled'].includes(res.data.status)) {
        stopPolling();
        setKeepaPulling(false);
        localStorage.removeItem('keepa-active-job-id');
        loadProducts();
      }
    } catch {
      // silently retry
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling]);

  const startJobPolling = (jobId: string) => {
    setKeepaJobId(jobId);
    localStorage.setItem('keepa-active-job-id', jobId);
    stopPolling();
    pollKeepaProg(jobId);
    pollRef.current = setInterval(() => pollKeepaProg(jobId), 2000);
  };

  const handleStartKeepaPull = async () => {
    setKeepaPulling(true);
    setKeepaProgress(null);
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    try {
      const res = await startKeepaPull(keepaMarketplace, undefined, ids);
      startJobPolling(res.data.job_id);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Failed to start Keepa pull. Check server logs.', 'error');
      setKeepaPulling(false);
    }
  };

  const handleRunUnmatched = async () => {
    setKeepaPulling(true);
    setKeepaProgress(null);
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    try {
      const res = await runUnmatched(keepaMarketplace, ids);
      startJobPolling(res.data.job_id);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Failed to start unmatched search. Check server logs.', 'error');
      setKeepaPulling(false);
    }
  };

  const [keepaCancelling, setKeepaCancelling] = useState(false);

  const handleStopKeepaPull = async () => {
    if (!keepaJobId) return;
    setKeepaCancelling(true);
    try {
      const res = await cancelJob(keepaJobId);
      if (res.data?.detail === 'Job already finished') {
        showToast('Job already finished', 'info');
      } else {
        showToast('Stopping after current batch…', 'info');
      }
    } catch {
      showToast('Job may have already finished', 'info');
    } finally {
      setKeepaCancelling(false);
    }
  };

  const keepaIsActive = keepaProgress && !['completed', 'failed', 'cancelled'].includes(keepaProgress.status);
  const keepaPct = keepaProgress && keepaProgress.total > 0
    ? Math.round((keepaProgress.processed / keepaProgress.total) * 100)
    : 0;

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortBy === col;
    return (
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10, userSelect: 'none' }}>
        {active && sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const thStyle = (col: string): React.CSSProperties => ({
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    background: sortBy === col ? 'var(--bg-tertiary)' : undefined,
  });

  return (
    <div>
      <div className="page-header">
        <h2>Products</h2>
        <p>{total} products in catalog</p>
      </div>

      {/* Keepa Pull Progress */}
      {keepaProgress && (
        <div className="card job-progress-card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: '-2px' }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Keepa Data Pull
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {keepaIsActive && (
                <button
                  className="btn btn-danger btn-small"
                  onClick={handleStopKeepaPull}
                  disabled={keepaCancelling}
                  style={{ padding: '2px 10px', fontSize: '11px' }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4, verticalAlign: '-1px' }}>
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                  {keepaCancelling ? 'Stopping…' : 'Stop'}
                </button>
              )}
              <span className={`job-status-badge ${
                keepaProgress.status === 'completed' ? 'job-status-completed' :
                keepaProgress.status === 'failed' ? 'job-status-failed' :
                keepaProgress.status === 'cancelled' ? 'job-status-failed' :
                keepaProgress.status === 'waiting_for_tokens' ? 'job-status-waiting' :
                'job-status-processing'
              }`}>
                {keepaProgress.status === 'waiting_for_tokens' ? 'Waiting for Tokens' :
                 keepaProgress.status === 'completed' ? 'Completed' :
                 keepaProgress.status === 'failed' ? 'Failed' :
                 keepaProgress.status === 'cancelled' ? 'Stopped' :
                 keepaProgress.status === 'queued' ? 'Queued' : 'Processing'}
              </span>
            </div>
          </div>

          <div className="job-progress-bar-container">
            <div className="job-progress-bar">
              <div
                className={`job-progress-fill ${keepaProgress.status === 'waiting_for_tokens' ? 'pulsing' : ''}`}
                style={{ width: `${keepaPct}%` }}
              />
            </div>
            <div className="job-progress-text">
              <span>{keepaProgress.processed} / {keepaProgress.total} products ({keepaPct}%)</span>
              {keepaIsActive && keepaProgress.eta_seconds != null && keepaProgress.eta_seconds > 0 && (
                <span className="job-eta">
                  ~{keepaProgress.eta_seconds < 60 ? `${keepaProgress.eta_seconds}s` : `${Math.floor(keepaProgress.eta_seconds / 60)}m ${keepaProgress.eta_seconds % 60}s`} remaining
                </span>
              )}
            </div>
          </div>

          <div className="job-stats-grid">
            <div className="job-stat">
              <div className="job-stat-value job-stat-matched">{keepaProgress.matched}</div>
              <div className="job-stat-label">Matched</div>
            </div>
            <div className="job-stat">
              <div className="job-stat-value job-stat-review">{keepaProgress.enriched || 0}</div>
              <div className="job-stat-label">Enriched</div>
            </div>
            <div className="job-stat">
              <div className="job-stat-value job-stat-nomatch">{keepaProgress.no_match}</div>
              <div className="job-stat-label">No Match</div>
            </div>
            <div className="job-stat">
              <div className="job-stat-value job-stat-failed">{keepaProgress.failed}</div>
              <div className="job-stat-label">Failed</div>
            </div>
          </div>

          {keepaProgress.status === 'waiting_for_tokens' && (
            <div className="job-token-wait">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Keepa API token limit reached. Waiting for tokens to replenish...</span>
            </div>
          )}

          {keepaProgress.status === 'completed' && (
            <div className="job-completed-msg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>
                All {keepaProgress.total} products processed.
                {keepaProgress.enriched > 0 && ` ${keepaProgress.enriched} enriched with Keepa data.`}
                {keepaProgress.matched > 0 && ` ${keepaProgress.matched} matched with ASINs.`}
                {keepaProgress.no_match > 0 && ` ${keepaProgress.no_match} not found on Amazon.`}
              </span>
            </div>
          )}

          {keepaProgress.status === 'cancelled' && (
            <div className="job-completed-msg" style={{ borderColor: 'var(--warning)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <rect x="9" y="9" width="6" height="6" rx="1" fill="var(--warning)" />
              </svg>
              <span>
                Stopped at {keepaProgress.processed} of {keepaProgress.total} products.
                {keepaProgress.enriched > 0 && ` ${keepaProgress.enriched} enriched.`}
                {keepaProgress.matched > 0 && ` ${keepaProgress.matched} matched.`}
                {' '}Products already processed keep their data.
              </span>
            </div>
          )}

          {keepaProgress.errors.length > 0 && (
            <div className="job-errors">
              <div className="job-errors-header">Errors ({keepaProgress.errors.length}):</div>
              <div className="job-errors-list">
                {keepaProgress.errors.slice(0, 5).map((err, i) => (
                  <div key={i} className="job-error-item">{err}</div>
                ))}
                {keepaProgress.errors.length > 5 && (
                  <div className="job-error-item" style={{ fontStyle: 'italic' }}>
                    ...and {keepaProgress.errors.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="filters-bar" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <CustomSelect
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val); setPage(1); }}
          placeholder="All Statuses"
          style={{ minWidth: 170 }}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'pending', label: 'Pending', color: 'var(--text-muted)' },
            { value: 'in_progress', label: 'In Progress', color: 'var(--info)' },
            { value: 'matched', label: 'Matched', color: 'var(--success)' },
            { value: 'needs_review', label: 'Needs Review', color: 'var(--warning)' },
            { value: 'no_match', label: 'No Match', color: 'var(--danger)' },
          ]}
        />
        <input
          className="search-input"
          placeholder="Search SKU, title, brand, EAN…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ minWidth: '240px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            className="btn btn-primary btn-small"
            onClick={() => setPullModalOpen(true)}
            disabled={keepaPulling || !!keepaIsActive}
            title={selectedIds.size > 0 ? `Pull Keepa data for ${selectedIds.size} selected product(s)` : 'Pull Keepa data for all products'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            {keepaPulling ? 'Pulling...' : selectedIds.size > 0 ? `Pull ${selectedIds.size} Selected` : 'Pull Keepa Data'}
          </button>
        </div>
        {selectedIds.size > 0 && (
          <button
            className="btn btn-danger btn-small"
            onClick={handleDeleteSelected}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
          </button>
        )}
        {total > 0 && (
          <button
            className="btn btn-danger btn-small"
            onClick={handleDeleteAll}
            disabled={deleting}
            style={{ marginLeft: 'auto' }}
          >
            {deleting ? 'Deleting...' : `Delete All (${total})`}
          </button>
        )}
        <ColumnToggle tableKey="product_list" columns={PRODUCT_LIST_COLUMNS} />
      </div>

      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner"></div> Loading products...</div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <p style={{ fontSize: '24px', marginBottom: '8px' }}>No Products</p>
            <p>No products found. Add products or adjust filters.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={products.length > 0 && selectedIds.size === products.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  {col('thumbnail') && <th style={{ width: '50px' }}></th>}
                  {col('sku') && <th style={thStyle('sku')} onClick={() => handleSort('sku')}>SKU<SortIcon col="sku" /></th>}
                  {col('title') && <th style={thStyle('title')} onClick={() => handleSort('title')}>Title<SortIcon col="title" /></th>}
                  {col('brand') && <th style={thStyle('brand')} onClick={() => handleSort('brand')}>Brand<SortIcon col="brand" /></th>}
                  {col('ean') && <th style={thStyle('ean')} onClick={() => handleSort('ean')}>EAN<SortIcon col="ean" /></th>}
                  {col('price') && <th style={thStyle('price')} onClick={() => handleSort('price')}>Price<SortIcon col="price" /></th>}
                  {col('buybox') && <th style={thStyle('buybox')} onClick={() => handleSort('buybox')}>Buy Box<SortIcon col="buybox" /></th>}
                  {col('fba_fee') && <th style={thStyle('fba_fee')} onClick={() => handleSort('fba_fee')}>FBA Fee<SortIcon col="fba_fee" /></th>}
                  {col('referral_fee') && <th style={thStyle('referral_fee')} onClick={() => handleSort('referral_fee')}>Referral Fee<SortIcon col="referral_fee" /></th>}
                  {col('profit_amount') && <th>Profit</th>}
                  {col('profit_margin') && <th>Profit %</th>}
                  {col('suggested') && <th>Suggested</th>}
                  {col('status') && <th style={thStyle('status')} onClick={() => handleSort('status')}>Status<SortIcon col="status" /></th>}
                  <th style={{ width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p: any) => (
                  (() => {
                    const symbol = getCurrencySymbolByCode(p.pricing_marketplace_code || keepaMarketplace);
                    return (
                  <tr key={p.id} onClick={() => onSelectProduct(p.id)} style={{ cursor: 'pointer' }}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                      />
                    </td>
                    {col('thumbnail') && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt=""
                            className="product-thumb product-thumb--clickable"
                            onClick={() => setLightboxUrl(p.image_url)}
                          />
                        ) : (
                          <div className="product-thumb-placeholder">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </div>
                        )}
                      </td>
                    )}
                    {col('sku') && <td><code>{p.internal_sku}</code></td>}
                    {col('title') && (
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.title}
                      </td>
                    )}
                    {col('brand') && <td>{p.brand || '\u2014'}</td>}
                    {col('ean') && <td><code>{p.ean || '\u2014'}</code></td>}
                    {col('price') && (
                      <td>
                        {p.price != null ? (
                          <span style={{ color: 'var(--text-primary)' }}>{symbol}{Number(p.price).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('buybox') && (
                      <td>
                        {p.buybox_price != null ? (
                          <span className="buybox-price">{symbol}{Number(p.buybox_price).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('fba_fee') && (
                      <td>
                        {p.fba_fee != null ? (
                          <span>{symbol}{Number(p.fba_fee).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('referral_fee') && (
                      <td>
                        {p.referral_fee != null ? (
                          <span>{symbol}{Number(p.referral_fee).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('profit_amount') && (
                      <td>
                        {p.profit_amount != null ? (
                          <span style={{ color: Number(p.profit_amount) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                            {symbol}{Number(p.profit_amount).toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('profit_margin') && (
                      <td>
                        {p.profit_margin_pct != null ? (
                          <span style={{ color: Number(p.profit_margin_pct) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                            {Number(p.profit_margin_pct).toFixed(2)}%
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('suggested') && (
                      <td>
                        {p.suggested_price != null ? (
                          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
                            {symbol}{Number(p.suggested_price).toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    {col('status') && <td><span className={`badge badge-${p.match_status}`}>{p.match_status}</span></td>}
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-danger btn-small"
                        onClick={(e) => handleDeleteSingle(e, p.id)}
                        disabled={deleting}
                        title="Delete product"
                        style={{ padding: '2px 8px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary btn-small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button className="btn btn-secondary btn-small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        )}
      </div>

      {/* ── Image lightbox ──────────────────────────────────────────── */}
      {lightboxUrl && (
        <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
          <button className="lightbox-close" onClick={() => setLightboxUrl(null)} title="Close (Esc)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {pullModalOpen && (
        <KeepaPullModal
          productIds={selectedIds.size > 0 ? Array.from(selectedIds) : undefined}
          contextLabel={selectedIds.size > 0 ? `${selectedIds.size} selected product(s)` : 'All products'}
          onClose={() => setPullModalOpen(false)}
          onStarted={(jobIds) => {
            setPullModalOpen(false);
            if (jobIds.length > 0) {
              setKeepaPulling(true);
              setKeepaProgress(null);
              startJobPolling(jobIds[jobIds.length - 1]);
              showToast(`Keepa pull started for ${jobIds.length} marketplace(s)`, 'info');
            }
          }}
        />
      )}
    </div>
  );
};

export default ProductList;
